/**
 * Phase 14c — pure value types for the crash-reporting + crash-free
 * session-rate measurement. The domain layer owns the *shape* of a
 * crash event and a session; the seams own the transport (Sentry /
 * GlitchTip / Axiom error-events) and the platform globals (browser
 * `Error.stack`, Node `process.on("uncaughtException")`, etc.).
 *
 * `MVP.md` §6 DoD item 2 reads: "Crash-free session rate >=99.5%
 * across supported OS versions for seven consecutive days." The
 * domain captures the math; the seam captures the transport; the
 * release-gate dashboard reads the resulting metric.
 *
 * Pinned dual-audience terminology per the
 * `feedback_dual_audience_terminology.md` memory:
 *
 *   - **Crash** — an unhandled exception that interrupts a user
 *     session. The web shell's TanStack Router `errorComponent`
 *     fires on a render-time throw; the API's tRPC error mapping
 *     fires on a procedure-handler throw that is not a typed domain
 *     error. Caught + handled errors (e.g., `IllegalPanelError`
 *     surfaced through `BAD_REQUEST`) are **not** crashes.
 *   - **Session** — a stable identifier scoped to one user-visible
 *     experience. On the web client, a session begins on app boot
 *     (router mount) and ends on tab close / SW unregister. On the
 *     API, a session is a single HTTP request (request-id).
 *   - **Crash-free session rate** — `(sessions - crashedSessions) /
 *     sessions`. Counted over a rolling window. The DoD bar is
 *     >=99.5% over a 7-day window across supported OS versions.
 */

/**
 * Severity of a captured crash event. Sentry conventions are
 * `fatal` / `error` / `warning` / `info` / `debug`; we expose the
 * three the codebase needs (`error` is the canonical crash; `fatal`
 * is reserved for SW boot / Clerk JWKS failures that prevent the
 * shell from rendering; `warning` covers handled-but-noteworthy
 * conditions the dashboard wants to track).
 */
export type CrashSeverity = "fatal" | "error" | "warning";

/**
 * The set of supported OS / browser surfaces the DoD measures across.
 * `MVP.md` §6 item 2 names "supported OS versions" without enumerating;
 * the carry-forward in `NEXT.md` 14c pins the set: iOS Safari >=16,
 * Android Chrome >=120, desktop evergreen. The pure helper distills
 * a user-agent string into one of these arms; rendering the
 * release-gate dashboard splits by this dimension.
 */
export type SupportedSurface =
  | "ios-safari"
  | "android-chrome"
  | "desktop-evergreen"
  | "unknown";

/**
 * One crash-report event. Mirrors a subset of Sentry's event shape so
 * a vendor-direct transport can pass these through verbatim; vendor
 * extras (breadcrumbs, span context) layer above this in the seam
 * adapter, not in the domain.
 */
export type CrashEvent = {
  /** Stable id minted at capture time. */
  readonly eventId: string;
  /** ISO 8601 timestamp of capture. */
  readonly capturedAt: string;
  readonly severity: CrashSeverity;
  /** The thrown value, normalized: `Error.name`, `Error.message`, `Error.stack`. */
  readonly error: {
    readonly name: string;
    readonly message: string;
    readonly stack: string | undefined;
  };
  /**
   * The session id active at the moment of capture. The crash-free
   * session-rate math counts a session as crashed if at least one
   * `severity === "fatal" | "error"` event ties to it.
   */
  readonly sessionId: string;
  /**
   * The "release" string (build sha or version tag) so the dashboard
   * can drop a hotfix's crashes off the rolling window after a
   * release.
   */
  readonly release: string;
  /**
   * Surface arm the event was captured on. The dashboard splits the
   * rate by this dimension to satisfy the "across supported OS
   * versions" clause of `MVP.md` §6 item 2.
   */
  readonly surface: SupportedSurface;
  /**
   * The user-agent the event was captured under, verbatim. Diagnostic
   * only; the surface arm is the canonical pivot.
   */
  readonly userAgent: string;
  /**
   * Free-form context: `route`, `procedure`, `requestId`, etc. The
   * seam adapter passes this through to the vendor.
   */
  readonly context: Readonly<Record<string, unknown>>;
};

/**
 * One session record. Created on boot / request entry; the
 * `crashedAt` is set when the first crash event ties to the session.
 *
 * The dashboard reads `Session` rows directly; the `CrashEvent` rows
 * are kept for forensic detail but the rate math runs over sessions.
 */
export type Session = {
  readonly sessionId: string;
  readonly startedAt: string;
  /** Set when a fatal / error event ties to this session. */
  readonly crashedAt: string | undefined;
  readonly surface: SupportedSurface;
  readonly release: string;
};

/**
 * Classify a user-agent string into a `SupportedSurface`. Pure
 * function — no `navigator.userAgent` reach. Tests pass exact UA
 * strings; the browser seam reads `navigator.userAgent` once at
 * boot and passes it in.
 *
 * Detection order matters: iOS Safari precedes Android Chrome
 * (Chrome-on-iOS reports `CriOS` + `Safari`, both substrings present);
 * the Chrome-on-iOS arm is captured under `ios-safari` because Apple
 * forces all iOS browsers to use WebKit, so the crash signature is
 * Safari's. `desktop-evergreen` catches modern desktop Chrome /
 * Firefox / Safari / Edge. Everything else falls through to
 * `unknown` — the dashboard surfaces those separately so a
 * regression on an unexpected surface is visible.
 */
export const classifySurface = (userAgent: string): SupportedSurface => {
  const ua = userAgent.toLowerCase();
  // iOS Safari: contains `iphone` or `ipad` (Mac-vs-iPad distinction
  // not load-bearing here — both share WebKit on iOS / iPadOS).
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")) {
    return "ios-safari";
  }
  // Android Chrome: must include `android` *and* `chrome` (Android-
  // Firefox is rarer and falls into `unknown` for the dashboard pivot).
  if (ua.includes("android") && ua.includes("chrome")) {
    return "android-chrome";
  }
  // Desktop evergreen: macOS / Windows / Linux + a modern engine
  // marker. Edge / Chrome / Firefox / Safari all share the desktop
  // arm because the DoD bar is per-OS-class, not per-vendor.
  const hasDesktopOs =
    ua.includes("macintosh") ||
    ua.includes("windows") ||
    ua.includes("x11") ||
    ua.includes("linux");
  const hasModernEngine =
    ua.includes("chrome") ||
    ua.includes("firefox") ||
    ua.includes("safari") ||
    ua.includes("edg");
  if (hasDesktopOs && hasModernEngine) {
    return "desktop-evergreen";
  }
  return "unknown";
};

/**
 * Normalize a thrown value into the `error` shape on `CrashEvent`.
 * Pure; never reads platform globals. The browser seam wraps the
 * `errorComponent`'s `error` arg through this; the API seam wraps
 * the `catch (err)` in the error-mapping middleware through this.
 */
export const normalizeError = (
  thrown: unknown,
): { readonly name: string; readonly message: string; readonly stack: string | undefined } => {
  if (thrown instanceof Error) {
    return {
      name: thrown.name,
      message: thrown.message,
      stack: thrown.stack,
    };
  }
  if (typeof thrown === "string") {
    return { name: "Error", message: thrown, stack: undefined };
  }
  if (thrown === null || thrown === undefined) {
    return { name: "Error", message: "unknown error", stack: undefined };
  }
  return {
    name: "Error",
    message: safeStringify(thrown),
    stack: undefined,
  };
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};
